"use client";

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  FileCode,
  FolderTree,
  Hammer,
  LineChart,
  Save,
  Terminal,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/shadcn-components/ui/button";
import { Badge } from "@/shadcn-components/ui/badge";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shadcn-components/ui/resizable";
import { persistBuildFromCompile } from "@/lib/custom-indicators/build-persist";
import { ensureCompiledFromBuild } from "@/lib/custom-indicators/hydrate-build";
import { compileIndicatorProject, preloadIndicatorRuntime } from "@/lib/custom-indicators/runtime/IndicatorRuntimeManager";
import { useIndicatorLoggerStore } from "@/lib/custom-indicators/runtime/logger-store";
import type { CreateProjectDraft } from "@/lib/custom-indicators/create-draft";
import type { CustomIndicatorRecord } from "@/lib/custom-indicators/types";
import {
  useCustomIndicatorProjectStore,
  useCustomIndicatorsStore,
} from "@/lib/custom-indicators/store";
import { getEntryContent } from "@/lib/custom-indicators/project-fs";
import { cn } from "@/lib/utils";
import CodeEditor from "./CodeEditor";
import CustomIndicatorAssetPreview from "./CustomIndicatorAssetPreview";
import CustomIndicatorIcon from "./CustomIndicatorIcon";
import CustomIndicatorPreviewChart from "./CustomIndicatorPreviewChart";
import CustomIndicatorEditorNavigator from "./CustomIndicatorEditorNavigator";
import CustomIndicatorEditorEmptyView from "./CustomIndicatorEditorEmptyView";
import CustomIndicatorEditorTerminal from "./CustomIndicatorEditorTerminal";
import type { ChartPanelId } from "@/vybe-trader-client/chart/stores/chartPanelTypes";

interface CustomIndicatorEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing project — opens from IndexedDB */
  record?: CustomIndicatorRecord | null;
  /** New project draft from wizard — stays isNew until Save */
  createDraft?: CreateProjectDraft | null;
  chartPanelId?: ChartPanelId;
}

function PanelToggleButton({
  pressed,
  onClick,
  title,
  ariaLabel,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  title: string;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "h-7 w-7 shrink-0",
        pressed && "bg-primary/10 text-primary"
      )}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={pressed}
    >
      {children}
    </Button>
  );
}

function EditorResizableBody({
  showNavigator,
  showCode,
  showChart,
  showTerminal,
  chartPanelId,
  projectId,
  indicatorName,
}: {
  showNavigator: boolean;
  showCode: boolean;
  showChart: boolean;
  showTerminal: boolean;
  chartPanelId?: ChartPanelId;
  projectId?: string;
  indicatorName: string;
}) {
  const activeEditorTarget = useCustomIndicatorProjectStore(
    (s) => s.activeEditorTarget
  );
  const files = useCustomIndicatorProjectStore((s) => s.files);
  const dirtyFiles = useCustomIndicatorProjectStore((s) => s.dirtyFiles);
  const project = useCustomIndicatorProjectStore((s) => s.project);
  const getActiveContent = useCustomIndicatorProjectStore(
    (s) => s.getActiveContent
  );
  const getActiveLanguage = useCustomIndicatorProjectStore(
    (s) => s.getActiveLanguage
  );
  const getActiveEditorTitle = useCustomIndicatorProjectStore(
    (s) => s.getActiveEditorTitle
  );
  const isViewingSdkDoc = useCustomIndicatorProjectStore(
    (s) => s.isViewingSdkDoc
  );
  const updateActiveFileContent = useCustomIndicatorProjectStore(
    (s) => s.updateActiveFileContent
  );
  const isLoading = useCustomIndicatorProjectStore((s) => s.isLoading);

  const activeContent = getActiveContent();
  const activeLanguage = getActiveLanguage();
  const editorTitle = getActiveEditorTitle();
  const readOnlyDoc = isViewingSdkDoc();
  const activeFilePath =
    activeEditorTarget.kind === "file" ? activeEditorTarget.path : "";
  const editorPaneKey =
    activeEditorTarget.kind === "file"
      ? activeEditorTarget.path
      : `sdk-doc:${activeEditorTarget.docId}`;

  const entryCode = useMemo(() => {
    if (!project) return "";
    const merged = files.map((f) => {
      const dirty = dirtyFiles[f.path];
      return dirty && f.type === "file" ? { ...f, content: dirty.content } : f;
    });
    return getEntryContent(merged, project.entryFilePath);
  }, [files, dirtyFiles, project]);

  const showEditorRow = showCode || showChart;
  const hasAnyPanel =
    showNavigator || showEditorRow || showTerminal;

  if (!hasAnyPanel) {
    return <CustomIndicatorEditorEmptyView />;
  }

  const editorPanels: ReactNode[] = [];

  if (showCode) {
    editorPanels.push(
      <ResizablePanel
        key="code"
        defaultSize={showChart ? 50 : 100}
        minSize={15}
      >
        <div className="flex h-full min-h-0 flex-col border-r border-border">
          <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-1.5">
            <span className="truncate text-xs text-muted-foreground">
              {readOnlyDoc ? `SDK: ${editorTitle}` : editorTitle || "C# editor"}
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                Loading project…
              </div>
            ) : activeLanguage === "image" && activeFilePath ? (
              <CustomIndicatorAssetPreview
                path={activeFilePath}
                content={activeContent}
              />
            ) : (
              <CodeEditor
                key={editorPaneKey}
                value={activeContent}
                onChange={updateActiveFileContent}
                language={activeLanguage}
                readOnly={readOnlyDoc}
              />
            )}
          </div>
        </div>
      </ResizablePanel>
    );
  }

  if (showChart) {
    if (editorPanels.length > 0) {
      editorPanels.push(<ResizableHandle key="handle-before-chart" withHandle />);
    }
    editorPanels.push(
      <ResizablePanel
        key="chart"
        defaultSize={showCode ? 50 : 100}
        minSize={15}
      >
        <CustomIndicatorPreviewChart
          panelId={chartPanelId}
          projectId={projectId}
          code={entryCode}
          indicatorName={indicatorName}
        />
      </ResizablePanel>
    );
  }

  const mainAreaPanels: ReactNode[] = [];

  if (showEditorRow) {
    mainAreaPanels.push(
      <ResizablePanel
        key="editor-row"
        defaultSize={showTerminal ? 72 : 100}
        minSize={20}
      >
        {editorPanels.length > 0 ? (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {editorPanels}
          </ResizablePanelGroup>
        ) : (
          <CustomIndicatorEditorEmptyView />
        )}
      </ResizablePanel>
    );
  }

  if (showTerminal) {
    if (mainAreaPanels.length > 0) {
      mainAreaPanels.push(
        <ResizableHandle key="handle-before-terminal" withHandle />
      );
    }
    mainAreaPanels.push(
      <ResizablePanel
        key="terminal"
        defaultSize={showEditorRow ? 28 : 100}
        minSize={10}
      >
        <CustomIndicatorEditorTerminal />
      </ResizablePanel>
    );
  }

  const mainArea =
    mainAreaPanels.length > 0 ? (
      <ResizablePanelGroup direction="vertical" className="h-full">
        {mainAreaPanels}
      </ResizablePanelGroup>
    ) : (
      <CustomIndicatorEditorEmptyView />
    );

  const outerPanels: ReactNode[] = [];

  if (showNavigator) {
    outerPanels.push(
      <ResizablePanel key="navigator" defaultSize={22} minSize={15}>
        <CustomIndicatorEditorNavigator />
      </ResizablePanel>
    );
  }

  if (showNavigator && (showEditorRow || showTerminal)) {
    outerPanels.push(
      <ResizableHandle key="handle-before-main" withHandle />
    );
  }

  if (showEditorRow || showTerminal) {
    outerPanels.push(
      <ResizablePanel
        key="main-area"
        defaultSize={showNavigator ? 78 : 100}
        minSize={30}
      >
        {mainArea}
      </ResizablePanel>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full">
      {outerPanels}
    </ResizablePanelGroup>
  );
}

export default function CustomIndicatorEditorDialog({
  open,
  onOpenChange,
  record,
  createDraft,
  chartPanelId,
}: CustomIndicatorEditorDialogProps) {
  const reload = useCustomIndicatorsStore((s) => s.reload);
  const isEdit = Boolean(record?.id);
  const isCreateDraft = Boolean(createDraft) && !record?.id;

  const project = useCustomIndicatorProjectStore((s) => s.project);
  const files = useCustomIndicatorProjectStore((s) => s.files);
  const dirtyFiles = useCustomIndicatorProjectStore((s) => s.dirtyFiles);
  const openNew = useCustomIndicatorProjectStore((s) => s.openNew);
  const openExisting = useCustomIndicatorProjectStore((s) => s.openExisting);
  const reset = useCustomIndicatorProjectStore((s) => s.reset);
  const save = useCustomIndicatorProjectStore((s) => s.save);
  const isSaving = useCustomIndicatorProjectStore((s) => s.isSaving);
  const storeError = useCustomIndicatorProjectStore((s) => s.error);
  const getCsprojContent = useCustomIndicatorProjectStore((s) => s.getCsprojContent);
  const getCompileFiles = useCustomIndicatorProjectStore((s) => s.getCompileFiles);
  const updateIconDataUrlFromCompile = useCustomIndicatorProjectStore(
    (s) => s.updateIconDataUrlFromCompile
  );
  const isCompiling = useIndicatorLoggerStore((s) => s.isCompiling);

  const entryCode = useMemo(() => {
    if (!project) return "";
    const merged = files.map((f) => {
      const dirty = dirtyFiles[f.path];
      return dirty && f.type === "file" ? { ...f, content: dirty.content } : f;
    });
    return getEntryContent(merged, project.entryFilePath);
  }, [files, dirtyFiles, project]);

  const [showNavigator, setShowNavigator] = useState(true);
  const [showCode, setShowCode] = useState(true);
  const [showChart, setShowChart] = useState(true);
  const [showTerminal, setShowTerminal] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }

    setShowNavigator(true);
    setShowCode(true);
    setShowChart(true);
    setShowTerminal(true);
    setSaveError(null);

    if (record?.id) {
      void openExisting(record.id).then(() => {
        void ensureCompiledFromBuild(record.id);
      });
    } else if (createDraft) {
      void openNew(createDraft);
    } else {
      void openNew();
    }
    preloadIndicatorRuntime();
  }, [
    open,
    record?.id,
    createDraft,
    openExisting,
    openNew,
    reset,
  ]);

  const handleCompile = useCallback(async () => {
    if (!project) return;
    setSaveError(null);
    try {
      const result = await compileIndicatorProject({
        projectId: project.id,
        csprojXml: getCsprojContent(),
        files: getCompileFiles(),
      });
      if (!result.success) {
        const firstError =
          (result.logs ?? []).find((l) => /error/i.test(l)) ??
          (result.logs ?? [])[(result.logs ?? []).length - 1];
        setSaveError(firstError ?? "Build failed — see Output tab");
      } else {
        if (result.icon?.dataUrl) {
          updateIconDataUrlFromCompile(result.icon.dataUrl);
        }
        await persistBuildFromCompile(project.id, result);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Compile failed");
    }
  }, [project, getCsprojContent, getCompileFiles, updateIconDataUrlFromCompile]);

  const handleExit = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    const result = await save();
    if (!result.ok) {
      setSaveError(result.errors[0] ?? "Save failed");
      return;
    }
    await reload();
  }, [save, reload]);

  const layoutKey = `${showNavigator}-${showCode}-${showChart}-${showTerminal}`;
  const displayName = project?.name ?? "My Custom Indicator";
  const displayVersion = project?.version ?? "1.0";
  const headerError = saveError ?? storeError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="left-[2vw] top-[2vh] flex h-[96vh] max-h-[96vh] w-[96vw] max-w-[96vw] translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-[96vw]"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <VisuallyHidden.Root asChild>
          <DialogTitle>Custom indicator editor</DialogTitle>
        </VisuallyHidden.Root>
        <VisuallyHidden.Root asChild>
          <DialogDescription>
            Edit and compile C# custom indicator projects for the chart.
          </DialogDescription>
        </VisuallyHidden.Root>
        <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
          <CustomIndicatorIcon
            name={displayName}
            iconDataUrl={project?.iconDataUrl}
            size={28}
          />
          <span className="max-w-[260px] truncate text-sm font-medium text-foreground">
            {displayName}
          </span>
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            v{displayVersion}
          </Badge>
          <div className="min-w-0 flex-1">
            {headerError ? (
              <p className="truncate text-xs text-destructive">{headerError}</p>
            ) : (
              <p className="truncate text-xs text-muted-foreground">
                {isEdit
                  ? "Editing custom indicator project"
                  : isCreateDraft
                    ? "New project — save to persist all files"
                    : "New custom indicator project"}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <PanelToggleButton
              pressed={showNavigator}
              onClick={() => setShowNavigator((v) => !v)}
              title={
                showNavigator
                  ? "Hide project navigator"
                  : "Show project navigator"
              }
              ariaLabel="Toggle project navigator"
            >
              <FolderTree className="h-3.5 w-3.5" />
            </PanelToggleButton>
            <PanelToggleButton
              pressed={showCode}
              onClick={() => setShowCode((v) => !v)}
              title={
                showCode ? "Hide C# editor" : "Show C# editor"
              }
              ariaLabel="Toggle C# editor"
            >
              <FileCode className="h-3.5 w-3.5" />
            </PanelToggleButton>
            <PanelToggleButton
              pressed={showChart}
              onClick={() => setShowChart((v) => !v)}
              title={showChart ? "Hide chart preview" : "Show chart preview"}
              ariaLabel="Toggle chart preview"
            >
              <LineChart className="h-3.5 w-3.5" />
            </PanelToggleButton>
            <PanelToggleButton
              pressed={showTerminal}
              onClick={() => setShowTerminal((v) => !v)}
              title={
                showTerminal
                  ? "Hide terminal panel"
                  : "Show terminal panel"
              }
              ariaLabel="Toggle terminal panel"
            >
              <Terminal className="h-3.5 w-3.5" />
            </PanelToggleButton>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            title="Compile C# project (Roslyn in WebAssembly)"
            aria-label="Compile indicator"
            disabled={!project || isCompiling}
            onClick={() => void handleCompile()}
          >
            <Hammer className="h-3.5 w-3.5" />
            {isCompiling ? "Compiling…" : "Compile"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={handleExit}
          >
            <X className="h-3.5 w-3.5" />
            Exit
          </Button>
          <Button
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={() => void handleSave()}
            disabled={isSaving}
          >
            <Save className="h-3.5 w-3.5" />
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </header>

        <div key={layoutKey} className="min-h-0 flex-1">
          <EditorResizableBody
            showNavigator={showNavigator}
            showCode={showCode}
            showChart={showChart}
            showTerminal={showTerminal}
            chartPanelId={chartPanelId}
            projectId={project?.id}
            indicatorName={displayName}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
